/** 앱 — which app earns, which loses, over a period you pick (S2).
 *
 *  The app ledger the old dashboard drew with a fixed 30 days and no search.
 *  The period (`?range=`: 어제·7일·30일·90일 or a month), the search (`?q=`)
 *  and 수익 없음 포함 (`?zero=1`) live in the address, so 오늘's tiles can link
 *  here and a filtered list can be shared. A row opens the app's drawer
 *  (`/apps/:id`) over this list.
 *
 *  Spend, profit and ROAS follow the spend rule (lib/spend.ts): a dash while
 *  Google Ads refuses or is not linked, and no 흑자/적자 pill then — revenue
 *  alone cannot say an app is in the black. */

import { useEffect, useMemo, useState } from 'react'
import type { StatsRow } from '@/api/client'
import { rangeIsPartial, seoulToday } from '@/lib/dates'
import { count, percent, usd } from '@/lib/money'
import { useApps, useStats } from '@/lib/queries'
import { useSpend } from '@/lib/useSpend'
import { useOpenApp } from '@/lib/detail'
import { platformKey, profitKey, statusOf } from '@/lib/status'
import { cn } from '@/lib/utils'
import {
  AppIcon,
  DataTable,
  EmptyState,
  FilterBar,
  FilterSelect,
  Money,
  PageHeader,
  Pager,
  RangeFilter,
  RoasBar,
  SearchInput,
  StatusPill,
  TonePill,
  Won,
  useIsDesktop,
  useQueryParam,
  useRange,
  type Column,
} from '@/ui'

export const PAGE_SIZE = 50

export type SortKey = 'name' | 'revenue' | 'cost' | 'profit' | 'roas' | 'impressions' | 'ecpm' | 'match_rate'
type Dir = 'asc' | 'desc'

const SORT_LABEL: Record<SortKey, string> = {
  name: '이름',
  revenue: '수익',
  cost: '지출',
  profit: '순이익',
  roas: 'ROAS',
  impressions: '노출',
  ecpm: 'eCPM',
  match_rate: '매칭률',
}

/** Rows ordered by a column. Names sort in Korean order; a tie keeps the
 *  server's order (revenue, high to low). */
export function sortRows(rows: StatsRow[], key: SortKey, dir: Dir): StatsRow[] {
  const sign = dir === 'asc' ? 1 : -1
  return rows
    .map((r, n) => ({ r, n }))
    .sort((a, b) => {
      const c =
        key === 'name' ? (a.r.app_name ?? '').localeCompare(b.r.app_name ?? '', 'ko') : (a.r[key] as number) - (b.r[key] as number)
      return c * sign || a.n - b.n
    })
    .map(({ r }) => r)
}

/** Whether a row is idle: nothing earned and nothing spent in the period. */
const idle = (r: StatsRow) => profitKey(r) === 'idle'

function SortHead({ label, k, sort, onSort, align }: { label: string; k: SortKey; sort: { key: SortKey; dir: Dir }; onSort: (k: SortKey) => void; align?: 'right' }) {
  const on = sort.key === k
  return (
    <button
      type="button"
      onClick={() => onSort(k)}
      aria-label={`${label} 순으로 정렬`}
      className={cn('inline-flex items-center gap-1 hover:text-foreground', on && 'text-foreground', align === 'right' && 'flex-row-reverse')}
    >
      {label}
      <span aria-hidden className={cn('text-[10px]', !on && 'opacity-0')}>
        {sort.dir === 'asc' ? '▲' : '▼'}
      </span>
    </button>
  )
}

export default function AppList() {
  const { range, setRange, from, to, label } = useRange('30d')
  const [q, setQ] = useQueryParam('q')
  const [zero, setZero] = useQueryParam('zero')
  const withZero = zero === '1'
  const [sort, setSort] = useState<{ key: SortKey; dir: Dir }>({ key: 'revenue', dir: 'desc' })
  const [page, setPage] = useState(1)
  const desktop = useIsDesktop()
  const open = useOpenApp()
  const spend = useSpend()
  const stats = useStats({ by: 'app', from, to, include_zero: withZero })
  const apps = useApps()

  useEffect(() => setPage(1), [range, q, withZero, sort])

  const pkg = useMemo(() => new Map((apps.data ?? []).map((a) => [a.id, a.package_name])), [apps.data])
  const rows = stats.data?.rows
  const needle = q.trim().toLowerCase()
  const filtered = useMemo(() => {
    if (!rows) return undefined
    const kept = rows.filter(
      (r) => !needle || (r.app_name ?? '').toLowerCase().includes(needle) || (r.package_name ?? pkg.get(r.app_id ?? -1) ?? '').toLowerCase().includes(needle),
    )
    return sortRows(kept, sort.key, sort.dir)
  }, [rows, needle, pkg, sort])

  const onSort = (key: SortKey) => setSort((s) => (s.key === key ? { key, dir: s.dir === 'desc' ? 'asc' : 'desc' } : { key, dir: key === 'name' ? 'asc' : 'desc' }))
  // A dash cannot be ordered by.
  const spendKeys: SortKey[] = ['cost', 'profit', 'roas']
  const head = (k: SortKey, align?: 'right') =>
    !spend.shown && spendKeys.includes(k) ? SORT_LABEL[k] : <SortHead label={SORT_LABEL[k]} k={k} sort={sort} onSort={onSort} align={align} />

  const shown = spend.shown
  const hint = spend.hint
  const total = filtered?.length ?? 0
  const pageRows = filtered?.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

  // 수익 없음: the idle rows, plus (while they are hidden) the apps the
  // stats left out.
  const idleCount = rows ? rows.filter(idle).length + (withZero ? 0 : Math.max(0, (apps.data?.length ?? 0) - rows.length)) : 0
  const surplus = rows?.filter((r) => !idle(r) && r.profit >= 0).length ?? 0
  const deficit = rows?.filter((r) => r.profit < 0).length ?? 0
  const earning = rows?.filter((r) => r.revenue > 0).length ?? 0

  const columns: Column<StatsRow>[] = [
    {
      key: 'app',
      header: head('name'),
      card: 'title',
      cell: (a) => (
        <span className="flex min-w-0 items-center gap-2.5">
          <AppIcon name={a.app_name ?? '?'} size={28} />
          <span className="min-w-0">
            <span className="block max-w-[18rem] truncate font-medium" title={a.app_name}>
              {a.app_name}
            </span>
            <span className="block text-meta text-muted-foreground">{statusOf('platform', platformKey(a.platform)).label}</span>
          </span>
        </span>
      ),
    },
    { key: 'revenue', header: head('revenue', 'right'), align: 'right', card: 'hidden', cell: (a) => <Money value={a.revenue} krw /> },
    { key: 'cost', header: head('cost', 'right'), align: 'right', card: 'hidden', cell: (a) => <Money value={a.cost} hidden={!shown} hint={hint} /> },
    { key: 'profit', header: head('profit', 'right'), align: 'right', card: 'hidden', cell: (a) => <Money value={a.profit} hidden={!shown} hint={hint} /> },
    { key: 'roas', header: head('roas'), card: 'hidden', cell: (a) => <RoasBar value={a.roas} cost={a.cost} hidden={!shown} hint={hint} /> },
    { key: 'impressions', header: head('impressions', 'right'), align: 'right', card: 'hidden', cell: (a) => <span className="tabular-nums">{count(a.impressions)}</span> },
    { key: 'ecpm', header: head('ecpm', 'right'), align: 'right', card: 'hidden', cell: (a) => <Money value={a.ecpm} /> },
    {
      key: 'match',
      header: head('match_rate', 'right'),
      align: 'right',
      card: 'hidden',
      cell: (a) => <span className="tabular-nums">{a.ad_requests > 0 ? percent(a.match_rate) : '—'}</span>,
    },
    ...(shown
      ? [{ key: 'status', header: '상태', card: 'meta', cell: (a) => <StatusPill domain="profit" status={profitKey(a)} /> } satisfies Column<StatsRow>]
      : []),
    {
      key: 'summary',
      header: '수익 · 순이익',
      cardOnly: true,
      card: 'sub',
      cell: (a) => (
        <span className="tabular-nums">
          수익 <span className="whitespace-nowrap text-foreground">{usd(a.revenue)}</span><Won usd={a.revenue} className="ml-1" />
          {shown && (
            <>
              {' · '}순이익 <span className="whitespace-nowrap text-foreground">{usd(a.profit)}</span>
              {' · '}ROAS <span className="whitespace-nowrap text-foreground">{a.cost > 0 ? `${a.roas.toFixed(2)}×` : '—'}</span>
            </>
          )}
        </span>
      ),
    },
  ]

  const dates = from === to ? from : `${from} ~ ${to}`
  return (
    <>
      <PageHeader title="앱" description="AdMob 앱별 수익. 행을 누르면 앱 상세가 열린다.">
        <FilterBar>
          <RangeFilter range={range} onChange={setRange} />
          <SearchInput value={q} onChange={setQ} placeholder="앱 이름·패키지 찾기" />
          <label className="inline-flex h-8 cursor-pointer items-center gap-2 text-table">
            <input type="checkbox" checked={withZero} onChange={(e) => setZero(e.target.checked ? '1' : '')} className="h-4 w-4 accent-accent" />
            수익 없음 포함
          </label>
          {!desktop && (
            <FilterSelect
              label="정렬"
              value={`${sort.key}:${sort.dir}`}
              onChange={(v) => {
                const [key, dir] = v.split(':') as [SortKey, Dir]
                setSort({ key, dir })
              }}
              options={(Object.keys(SORT_LABEL) as SortKey[])
                .filter((k) => shown || !spendKeys.includes(k))
                .map((k) => ({ value: `${k}:${k === 'name' ? 'asc' : 'desc'}`, label: `${SORT_LABEL[k]} 순` }))}
            />
          )}
        </FilterBar>
      </PageHeader>

      <div className="mb-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-table text-muted-foreground">
        <span>
          {label} · {dates}
        </span>
        {rangeIsPartial(range, seoulToday()) && <TonePill tone="publishing" label="오늘은 집계 중" />}
        {rows && (
          <span className="tabular-nums" aria-label="요약">
            {shown ? (
              <>
                흑자 {surplus} · 적자 {deficit} · 수익 없음 {idleCount}
              </>
            ) : (
              <>
                수익 있음 {earning} · 수익 없음 {idleCount}
              </>
            )}
          </span>
        )}
        {!shown && spend.ready && hint && <span>{hint}</span>}
      </div>

      <div className="space-y-3">
        <DataTable
          label="앱 목록"
          columns={columns}
          rows={pageRows}
          rowKey={(a) => a.app_id ?? a.app_name ?? ''}
          loading={stats.isLoading}
          error={stats.error}
          onRetry={() => stats.refetch()}
          onRowClick={(a) => a.app_id !== undefined && open(a.app_id)}
          empty={
            <EmptyState
              title={needle ? '찾는 앱이 없다' : `${label} 동안 수익이 난 앱이 없다`}
              description={needle ? '이름이나 패키지를 다시 확인한다.' : withZero ? undefined : '수익 없음 포함을 켜면 전체 앱이 보인다.'}
            />
          }
        />
        <Pager total={total} page={page} pageSize={PAGE_SIZE} onPage={setPage} />
      </div>
    </>
  )
}
